import { useState } from 'react'
import Fab from '@material-ui/core/Fab'
import AddIcon from '@material-ui/icons/Add'
import arrayMutators from 'final-form-arrays'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Link from '@material-ui/core/Link'
import Grid from '@material-ui/core/Grid'
import { Field, Form, FormSpy } from 'react-final-form'
import { makeStyles } from '@material-ui/core/styles'
import { default as MuiTextField } from '@material-ui/core/TextField'
import FormRow from '../../pages/RecordPage/FormRow'
import DialogTitle from '@material-ui/core/DialogTitle'
import { client } from '../../client'
import validate from './validate'
import { Checkbox, DatePicker } from '../../react-final-form-wrappers'
import useEffectAsync from '../../hooks/useEffectAsync'
import { Autocomplete, Radios, TextField } from 'mui-rff'
import { validateEmail } from '../../utils/validate'
import setFieldData from 'final-form-set-field-data'
import { WarningTextField } from './WarningTextField'
import { matchSorter } from 'match-sorter'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { assert } from '../../utils/assert'
import { FunnelStatus, Insurer, Practice } from '../../types'
import { currentUserCanAccessPractice } from './currentUserCanAccessPractice'
import AuthenticationService from '../../services/AuthenticationService'


const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1),
    // margin: 0,
    top: 'auto',
    right: 20,
    bottom: 20,
    left: 'auto',
    position: 'fixed'
  }
}))

interface HandleSubmitParams {
  values: any
  setOpen: (open: boolean) => void
}

const handleSubmit = async ({ values, setOpen }: HandleSubmitParams) => {
  const {
    firstName,
    infix,
    lastName,
    email,
    phoneNumber,
    birthDate,
    status,
    practice,
    insurer,
    remarks,
    registrationDate,
    online,
    onsite,
    language
  } = values
  const response = await client.addFunnel({
    patient: { firstName, infix, lastName, email, phoneNumber, birthDate },
    status,
    practice,
    insurer,
    registrationDate,
    remarks,
    online,
    onsite,
    language
  })
  const href = `/wachtlijst/${response.data.id}`
  setOpen(false)
  window.location.href = href
}

/**
 * @returns {Promise<string | undefined>}
 */
const getPatientLink = async (patientId: number | undefined): Promise<string | undefined> => {
  if (typeof patientId !== 'number') return undefined
  const treatmentPrograms = await client.getTreatmentProgramsByPatientId(
    patientId
  )
  const treatmentId = Array.isArray(treatmentPrograms) && treatmentPrograms[0]
  if (treatmentId) {
    return `/clienten/${patientId}/trajecten/${treatmentId}`
  }
  const funnels = await client.getFunnelsByPatientId(patientId)
  assert(Array.isArray(funnels), 'getFunnelsByPatientId should return array')
  const funnel = funnels.shift()
  assert(!!funnel, 'patient should have at least one funnel')
  const funnelId = funnel.id
  assert(typeof funnelId === 'number', 'funnelId should be a number')
  return `/wachtlijst/${funnelId}`
}

interface WarningEngineProps {
  mutators: { setFieldData: any }
}

const WarningEngine = ({ mutators: { setFieldData } }: WarningEngineProps) => {
  const { t } = useTranslation('addPatientComponent')

  return (
    <FormSpy
      subscription={{ values: true }}
      onChange={async ({ values }) => {
        if (values.email && validateEmail(values.email)) {
          const data = await client.findPatientByEmailOrPhoneNumber({
            email: values.email
          })
          const link = await getPatientLink(data?.data?.patient?.id)
          setFieldData('email', {
            warning:
              typeof link === 'string' ? (
                <>
                  {t('emailTaken')}&nbsp;
                  {AuthenticationService.isBackOffice() ||
                  AuthenticationService.isAdministrator() ||
                  AuthenticationService.isSuperUser() ? (
                    <Link href={link}>{t('clickHere')}</Link>
                  ) : null}
                </>
              ) : undefined
          })
        }
        if (values.phoneNumber) {
          const data = await client.findPatientByEmailOrPhoneNumber({
            phoneNumber: values.phoneNumber
          })
          const link = await getPatientLink(data?.data?.patient?.id)
          setFieldData('phoneNumber', {
            warning:
              typeof link === 'string' ? (
                <>
                  {t('phoneNumberTaken')}&nbsp;
                  {AuthenticationService.isBackOffice() ||
                  AuthenticationService.isAdministrator() ||
                  AuthenticationService.isSuperUser() ? (
                    <Link href={link}>{t('clickHere')}</Link>
                  ) : null}
                </>
              ) : undefined
          })
        }
      }}
    />
  )
}

interface AddPatientFabProps {
  onSubmit?: (params: HandleSubmitParams) => Promise<void>
}

export const AddPatientFab = ({ onSubmit = handleSubmit }: AddPatientFabProps) => {
  const [open, setOpen] = useState(false)
  const classes = useStyles()
  const { t } = useTranslation('addPatientComponent')

  const [state, setState] = useState<{
    statusOptions: FunnelStatus[]
    insurerOptions: Insurer[]
    practiceOptions: Practice[]
  }>({
    statusOptions: [],
    insurerOptions: [],
    practiceOptions: []
  })
  const [loading, setLoading] = useState(true)

  useEffectAsync(async () => {
    const normalize = (s: string) =>
      (s || '')
        .replace(/\s+/g, ' ')
        .trim()

    const preferredOrder = [
      'KM ingepland via Calendly',
      'Doorverwezen via ZorgDomein',
      'Doorverwezen op andere wijze',
      'Per e-mail contact met ons opgenomen'
    ].map(normalize)

    let statusOptions = await client.getFunnelStatusOptions({ stage: 'INITIAL' })

    // Hide specific status and normalize display names
    statusOptions = statusOptions
      .filter((option: any) => normalize(option?.name) !== 'Bel-me-terugformulier ingevuld')
      .map((option: any) => {
        const nameNorm = normalize(option?.name)
        const displayName =
          nameNorm === 'Doorverwezen via Zorgdomein'
            ? 'Doorverwezen via ZorgDomein'
            : option?.name
        return { ...option, name: displayName, _sortKey: normalize(displayName) }
      })

    // Sort according to preferred order, then by backend "order"
    const rank = new Map(preferredOrder.map((n, i) => [n, i]))
    statusOptions = statusOptions.sort((a: any, b: any) => {
      const as = a._sortKey || normalize(a.name)
      const bs = b._sortKey || normalize(b.name)
      const ai = rank.has(as) ? (rank.get(as) as number) : Number.MAX_SAFE_INTEGER
      const bi = rank.has(bs) ? (rank.get(bs) as number) : Number.MAX_SAFE_INTEGER
      if (ai !== bi) return ai - bi
      const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER
      const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER
      return ao - bo
    })

    const insurerOptions = await client.getInsurerOptions()
    const filteredInsurerOptions = insurerOptions.filter(
      (option) => !option.disabled
    )

    const practiceOptions = await client.getPracticeOptions(true)

    setState({
      statusOptions,
      insurerOptions: filteredInsurerOptions,
      practiceOptions: practiceOptions?.filter((practice) =>
        currentUserCanAccessPractice(practice)
      )
    })
    setLoading(false)
  }, [])

  const filterOptions = (
    options: any[],
    { inputValue }: { inputValue: string }
  ) => (inputValue && inputValue.trim().length > 0
    ? matchSorter(options, inputValue, { keys: ['name'] })
    : options)

  if (loading) {
    return null
  }

  const languageOptions = [
    { label: t('dutch'), value: 'nl' },
    { label: t('english'), value: 'en' }
  ]

  return (
    <>
      <Fab
        onClick={() => setOpen(true)}
        color="primary"
        aria-label="Add"
        className={classes.root}
      >
        <AddIcon />
      </Fab>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="form-dialog-title"
      >
        <Form
          onSubmit={(values) => onSubmit({ values, setOpen })}
          subscription={{ submitting: true, pristine: true, invalid: true }}
          validate={validate}
          // Cast to any to satisfy differing Mutator generic in this form
          mutators={{
            setFieldData,
            ...arrayMutators
          } as any}
          initialValues={{
            patient: {},
            registrationDate: format(new Date(), 'yyyy-MM-dd')
          }}
          render={({ form, handleSubmit, pristine, invalid }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <DialogTitle>{t('addClient')}</DialogTitle>
              <DialogContent>
                <FormRow xs={[4, 3, 5]}>
                  <TextField
                    // required
                    fullWidth
                    name="firstName"
                    label={t('firstName')}
                  />
                  <TextField fullWidth name="infix" label={t('infix')} />
                  <TextField
                    // required
                    fullWidth
                    name="lastName"
                    label={t('lastName')}
                  />
                </FormRow>
                <FormRow xs={[6, 6]}>
                  <WarningTextField fullWidth name="email" label={t('email')} />
                  <WarningTextField
                    fullWidth
                    name="phoneNumber"
                    label={t('phoneNumber')}
                  />
                </FormRow>
                <FormRow xs={[6, 6]}>
                  <Autocomplete
                    fullWidth
                    name="status"
                    options={state.statusOptions}
                    // @ts-ignore
                    getOptionLabel={(option) => option && option.name}
                    getOptionSelected={(option, value) =>
                      // @ts-ignore
                      option ? option.id === value.id : false
                    }
                    // @ts-ignore
                    renderInput={(params) => (
                      <MuiTextField {...params} label="Status" />
                    )}
                    filterOptions={filterOptions}
                  />
                  <Autocomplete
                    fullWidth
                    name="practice"
                    options={state.practiceOptions}
                    // @ts-ignore
                    getOptionLabel={(option) => option && option.name}
                    getOptionSelected={(option, value) =>
                      // @ts-ignore
                      option ? option.id === value.id : false
                    }
                    // @ts-ignore
                    renderInput={(params) => (
                      <MuiTextField {...params} label={t('practice')} />
                    )}
                    filterOptions={filterOptions}
                  />
                </FormRow>
                <FormRow xs={[6, 6]}>
                  {/* <Autocomplete */}
                  {/*   fullWidth */}
                  {/*   name="insurer" */}
                  {/*   options={state.insurerOptions} */}
                  {/*   getOptionLabel={(option) => */}
                  {/*     option && */}
                  {/*     `${option.name}${ */}
                  {/*       option.note ? ' ' + option.note + ' ' : ' ' */}
                  {/*     }(${option.uzovi})` */}
                  {/*   } */}
                  {/*   getOptionSelected={(option, value) => */}
                  {/*     option ? option.id === value.id : false */}
                  {/*   } */}
                  {/*   renderInput={(params) => ( */}
                  {/*     <MuiTextField {...params} label={t('insurer')} /> */}
                  {/*   )} */}
                  {/*   filterOptions={filterOptions} */}
                  {/* /> */}
                  <Field
                    // style={{ width: 300 }}
                    name="birthDate"
                    label={t('birthDate')}
                    // @ts-ignore
                    component={DatePicker}
                  />
                  <Field
                    name="registrationDate"
                    label={t('registrationDate')}
                    // @ts-ignore
                    component={DatePicker}
                  />
                </FormRow>
                <FormRow>
                  <Grid container direction="row" spacing={5}>
                    <Grid item>
                      <Field
                        type="checkbox"
                        name="online"
                        label={t('online')}
                        // @ts-ignore
                        component={Checkbox}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        type="checkbox"
                        name="onsite"
                        label={t('onsite')}
                        // @ts-ignore
                        component={Checkbox}
                      />
                    </Grid>
                  </Grid>
                </FormRow>
                <FormRow>
                  <Radios
                    color="primary"
                    label={t('language')}
                    name="language"
                    data={languageOptions}
                    radioGroupProps={{
                      row: true
                    }}
                  />
                </FormRow>
                <FormRow>
                  <TextField
                    multiline
                    fullWidth
                    name="remarks"
                    label={t('remarks')}
                  />
                </FormRow>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpen(false)} color="primary">
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={pristine || invalid}
                >
                  {t('save')}
                </Button>
              </DialogActions>
              {/* @ts-ignore */}
              <WarningEngine mutators={form.mutators} />
            </form>
          )}
        />
      </Dialog>
    </>
  )
}
